import { supabase } from '../lib/supabase';

const parsePositiveAmount = (raw, fallback) => {
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : fallback;
};

const monthlyAmount = parsePositiveAmount(import.meta.env?.VITE_UROPAY_AMOUNT_MONTHLY, 2500);

const toCamelCase = (obj) => {
  if (!obj || typeof obj !== 'object') return obj;
  if (Array.isArray(obj)) return obj.map(toCamelCase);
  return Object.keys(obj).reduce((acc, key) => {
    const camelKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
    acc[camelKey] = toCamelCase(obj[key]);
    return acc;
  }, {});
};

const invokeWithAuth = async (functionName, body = {}) => {
  const { data: refreshed, error: refreshError } = await supabase.auth.refreshSession();
  const accessToken = refreshed?.session?.access_token;

  if (refreshError || !accessToken) {
    return {
      data: null,
      error: {
        message: 'Your session may have expired. Please log out, log in again, and try again.',
        code: 'SESSION_EXPIRED',
      },
    };
  }

  const { data, error } = await supabase.functions.invoke(functionName, {
    body,
    headers: {
      Authorization: `Bearer ${accessToken}`,
      apikey: import.meta.env?.VITE_SUPABASE_ANON_KEY || '',
    },
  });

  if (error) {
    return {
      data: null,
      error: { ...error, message: error?.message || 'Request failed. Please try again.' },
    };
  }

  return { data: toCamelCase(data), error: null };
};

export const uropayService = {
  plans: {
    monthly: { id: 'monthly', name: 'Premium Monthly', amount: monthlyAmount, period: 'month', months: 1 },
    yearly: { id: 'yearly', name: 'Premium Yearly', amount: 5000, period: 'year', months: 12 },
    lifetime: { id: 'lifetime', name: 'Premium Lifetime', amount: 10000, period: 'lifetime', months: null },
  },

  createPending(planId) {
    return invokeWithAuth('create-uropay-pending', { planId });
  },

  setReturned(payload) {
    return invokeWithAuth('set-uropay-returned', payload);
  },

  checkActivation(referenceNumber) {
    return invokeWithAuth('check-uropay-activation', { referenceNumber });
  },

  async getActiveSubscription(userId) {
    try {
      const { data, error } = await supabase
        .from('stripe_subscriptions')
        .select('*')
        .eq('user_id', userId)
        .in('status', ['active', 'trialing'])
        .order('created_at', { ascending: false })
        .limit(1);

      if (error) return { data: null, error };
      return { data: data?.length ? toCamelCase(data[0]) : null, error: null };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getPaymentHistory(userId, limit = 50) {
    try {
      const { data, error } = await supabase
        .from('stripe_payments')
        .select('*')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(limit);
      if (error) return { data: [], error };
      return { data: data ? toCamelCase(data) : [], error: null };
    } catch (error) {
      return { data: [], error };
    }
  },
};

export default uropayService;
